import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Lock, Mail, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button, Input, Label, Card } from '@/shared/ui';
import { FinceraWordmark } from '@/shared/ui/brand';
import { useAuthStore } from './auth-store';
import { apiClient } from '@/shared/api/client';
import type { Captcha } from '@/shared/types';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, status, error, clearError } = useAuthStore();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captcha, setCaptcha] = useState<Captcha | null>(null);
  const [captchaText, setCaptchaText] = useState('');

  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const submitting = status === 'authenticating';

  const loadCaptcha = useCallback(async () => {
    setCaptcha(null);
    setCaptchaText('');
    try {
      setCaptcha(await apiClient.getCaptcha());
    } catch {
      /* leave empty; user can hit refresh */
    }
  }, []);

  useEffect(() => {
    void loadCaptcha();
  }, [loadCaptcha]);

  useEffect(() => {
    if (status === 'authenticated') navigate(from, { replace: true });
  }, [status, from, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await login(username, password, captcha?.id ?? '', captchaText);
    } catch {
      // Captcha is single-use — fetch a fresh challenge after any failed attempt.
      void loadCaptcha();
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-base px-4">
      {/* Ambient brand glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-[-10%] size-[480px] -translate-x-1/2 rounded-full bg-brand-600/20 blur-[120px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
        className="relative w-full max-w-sm"
      >
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <FinceraWordmark />
          <h1 className="sr-only">Fincera</h1>
          <p className="text-sm text-muted-foreground">API Analyzer · sign in to continue</p>
        </div>

        <Card className="p-6">
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="username">Email</Label>
              <div className="relative">
                <Mail
                  className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint"
                  aria-hidden
                />
                <Input
                  id="username"
                  type="email"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    if (error) clearError();
                  }}
                  placeholder="you@fincera.ai"
                  className="pl-9"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock
                  className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint"
                  aria-hidden
                />
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) clearError();
                  }}
                  placeholder="••••••••"
                  className="pl-9"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="captcha">Captcha</Label>
              <div className="flex items-center gap-2">
                <div className="flex h-[50px] w-[150px] shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-white">
                  {captcha ? (
                    <img
                      src={`data:image/svg+xml;utf8,${encodeURIComponent(captcha.svg)}`}
                      alt="captcha challenge"
                      width={150}
                      height={50}
                    />
                  ) : (
                    <Loader2 className="size-4 animate-spin text-faint" aria-hidden />
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => void loadCaptcha()}
                  aria-label="Refresh captcha"
                  title="Refresh captcha"
                >
                  <RefreshCw className="size-4" aria-hidden />
                </Button>
              </div>
              <div className="relative">
                <ShieldCheck
                  className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint"
                  aria-hidden
                />
                <Input
                  id="captcha"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={captchaText}
                  onChange={(e) => {
                    setCaptchaText(e.target.value);
                    if (error) clearError();
                  }}
                  placeholder="Answer (add the two numbers)"
                  className="pl-9 tracking-widest"
                  required
                />
              </div>
            </div>

            {error && (
              <p
                role="alert"
                className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger"
              >
                {error}
              </p>
            )}

            <Button type="submit" size="lg" className="w-full" disabled={submitting || !captcha || !captchaText}>
              {submitting && <Loader2 className="size-4 animate-spin" aria-hidden />}
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </Card>
      </motion.div>
    </div>
  );
}
